import axios from "axios";
import { decrypt, encrypt } from "../helpers/encryptDecrypt";
import { baseUrl } from "../utils/constants";

// Inactivity timeout - must match AuthContext
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const _retrieveToken = async () => {
  const value = await localStorage.getItem("app-token");
  return value;
};

export const _retrieveTokenExpiry = async () => {
  const value = await localStorage.getItem("token-expiry");
  return value;
};

export const _retrieveForgotToken = async () => {
  const value = await localStorage.getItem("forgot_token");
  return value;
};

export const _removeData = async () => {
  try {
    await localStorage.removeItem("app-token");
    await localStorage.removeItem("user-role");
    await localStorage.removeItem("token-expiry");
  } catch (error) {
    console.log(error);
  }
};

// Update token expiry time (API call counts as user activity)
const updateTokenExpiryOnActivity = async () => {
  try {
    const newExpiryTime = Date.now() + INACTIVITY_TIMEOUT;
    const encryptedExpiry = encrypt(newExpiryTime.toString());
    await localStorage.setItem("token-expiry", JSON.stringify(encryptedExpiry));
    console.log("Token expiry updated on API call:", new Date(newExpiryTime).toLocaleString());
  } catch (error) {
    console.error("Error updating token expiry on API call:", error);
  }
};

const headers = {}

const http = axios.create({
  baseURL: baseUrl,
  headers: headers
});

const authHttp = axios.create({
  baseURL: baseUrl,
  headers: headers
});


// Response interceptor - handles 401 errors
authHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Check for token expiry in error response
    const res = error?.response;

    if (
      res &&
      (res.status === 401 || res.data?.status_code === 401)
    ) {
      error.isSessionExpired = true;
      console.log("error", error, "event dispatch");

      // Clear all auth data
      await _removeData();

      // Dispatch a custom event for token expiry
      window.dispatchEvent(new Event('tokenExpired'));
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);


// Request interceptor - adds token, checks expiry, and updates expiry on activity
authHttp.interceptors.request.use(
  async (config) => {
    try {
      // Check token expiry before making request
      const encryptedExpiry = await _retrieveTokenExpiry();
      if (encryptedExpiry) {
        const decryptedExpiry = decrypt(JSON.parse(encryptedExpiry));
        const expiryTime = parseInt(decryptedExpiry);

        if (Date.now() > expiryTime) {
          console.log("Token expired locally, dispatching event");
          await _removeData();
          window.dispatchEvent(new Event('tokenExpired'));
          return Promise.reject(new Error('Token expired'));
        }

        // IMPORTANT: Update token expiry since API call is user activity
        await updateTokenExpiryOnActivity();
      }

      // Decrypt and attach token to request
      const encryptedToken = await _retrieveToken();
      const accessToken = encryptedToken ? decrypt(JSON.parse(encryptedToken)) : null;

      if (encryptedToken !== null && typeof encryptedToken !== "undefined" && accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    } catch (error) {
      console.error("Error in request interceptor:", error);
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
);

export { authHttp };

export default http;
